import { act_com } from '@lc/action/cardType/Common';
import { carbonStolenThisTurn } from '@lc/const/modifiers';
import {
	buyBattery,
	buyFactory,
	buyPR,
	donateEnergy,
	endTurn,
	playCard,
	playSummitCard,
	playEquipmentById,
	storeEnergy,
	stealCarbon,
	makeDecision,
} from '@lc/const/socketEvent';
import { decisionCardResolution } from '@lc/const/turnTypes';
import { TUTORIAL_BLURBS } from '@lc/const/tutorial';
import { controller_card } from '@lc/controller/card';
import { controller_news } from '@lc/controller/news';
import { controller_timeline } from '@lc/controller/timeline';
import { GameManager } from '@lc/interface/gameManager/types';
import { GameState } from '@lc/interface/gameState/types';
import { act_payment } from '@lc/interface/payment/action';
import { act_player } from '@lc/interface/player/action';
import { createStateChangeEvent } from '@lc/interface/stateChangeEvent/factory';
import { StateChangeEvent } from '@lc/interface/stateChangeEvent/types';
import { getRandomArrayIndex } from '@lc/lib/array';
import { setStateForPRTurnDecision } from '@lc/lib/gamestate_helper';
import { getPlayerStateWithId } from '@lc/lib/player_helper';
import {
	applyPatchCustom,
	areObjectsIdentical,
	getTwoWayDiffCustom,
} from '@lc/lib/state';
import { chooseSummitCard } from '@lc/lib/summit_helper';
import { timeline_resolution_winter } from '@lc/lib/timeline/resolution_winter';
// import { isDeepStrictEqual } from 'node:util';
import { energyColors } from '@lc/types/Misc';

/**
 *
 * REMEMBER, THE TICKER RETURNING SOMETHING MEANS YOU DID SOMETHING AND THE STATE HAS BEEN ALTERED
 * DO I ADD "ACTION DID NOT WORK" TICKER RETURNS?
 *
 * Attempt to Perform Action
 * If "isGameStateUpdated", have a next() kind of function to check if it's AI turn to perform moves if single player game || if player's user id is an AI one
 *
 * @param socket
 * @param io
 */

class GameController {
	async addActionToTicker(pState: any, ticker: string[], action: string) {
		ticker.unshift(
			`Player ${pState.playerId} (${pState.username}) ${action}`
		);
	}

	async appendTickerToLatestStateChange(
		gameManager: GameManager,
		ticker: string[]
	) {
		const z = gameManager.stateChangeHistory.length - 1;

		gameManager.stateChangeHistory[z].ticker.push(...ticker);
	}

	// wrap function, save the before and after to the ticker row,
	// expand ticker table to have a before and current for a variable at proper level of nesting

	async logGameEvent(
		gameState: GameState,
		stateChangeEvent: StateChangeEvent, // new object, can we do a series of changes? ideally array of relevant ticker text but a before and after to tie to the state change?
		func: any
	) {
		// save prev state
		const oldGameState = structuredClone(gameState);
		await func(gameState, stateChangeEvent.ticker);
		// compare prev state and current state, save changes between the two
		const { forward, reverse } = getTwoWayDiffCustom(
			oldGameState,
			gameState
		);

		stateChangeEvent.prev_patch = reverse;
		stateChangeEvent.next_patch = forward;
	}

	// each action is a singular dive down to a state change
	// returns true/false whether state has changed at all
	async logGameStateChange(
		gameManager: GameManager,
		playerId: number,
		func: any,
		action: string = '',
		recordNoChange = true
	): Promise<boolean> {
		const debug = false;
		const { gameState } = gameManager;
		const stateChangeEvent = createStateChangeEvent();

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (action !== '') {
			const tickerText = `Player ${pState.playerId} (${pState.username}) ${action}`;
			stateChangeEvent.ticker.push(tickerText);
		}

		// save prev state
		if (debug) console.time('structuredClone');
		const oldGameState = structuredClone(gameState);
		if (debug) console.timeEnd('structuredClone');

		const stateChanged = await func(
			gameState,
			playerId,
			stateChangeEvent.ticker
		);
		// compare prev state and current state, save changes between the two

		if (debug) console.time('getTwoWayDiffCustom');
		const { forward, reverse } = getTwoWayDiffCustom(
			oldGameState,
			gameState
		);
		if (debug) console.timeEnd('getTwoWayDiffCustom');

		// test if state change has occured
		if (
			Object.keys(forward).length === 0 &&
			Object.keys(forward).length === 0
		) {
			return false;
		}

		const patchedState = applyPatchCustom(oldGameState, forward);

		if (debug) {
			console.log('logGameStateChange');

			console.log('oldGameState');
			console.dir(oldGameState, { depth: null });

			console.log('forward');
			console.dir(forward, { depth: null });
			console.log('reverse');
			console.dir(reverse, { depth: null });

			console.log('gameState');
			console.dir(gameState, { depth: null });

			console.log('post patch');
			console.dir(patchedState, { depth: null });
		}

		// if (isDeepStrictEqual(gameState, patchedState)) {
		if (areObjectsIdentical(gameState, patchedState)) {
			console.log('PATCHED STATE WORKS!!');
		} else {
			console.log('them patches aint right');
		}

		if (recordNoChange || stateChanged) {
			stateChangeEvent.prev_patch = reverse;
			stateChangeEvent.next_patch = forward;
			gameManager.stateChangeHistory.push(stateChangeEvent);
		}

		return true;
	}

	/* PRE GAME ACTIONS */

	async startGame(
		gameId: number,
		players: any[],
		stateChangeEvent: StateChangeEvent
	) {
		const gameState = controller_timeline.initializeGame(gameId, players); // initialize my state, send to all players

		await this.logGameEvent(
			gameState,
			stateChangeEvent,
			(gameState: GameState, ticker: string[]) =>
				controller_news.flipNews(gameState, ticker)
		);

		return gameState;
	}

	async startSinglePlayerGame(
		gameId: number,
		players: any[],
		stateChangeEvent: StateChangeEvent
	) {
		const gameState = controller_timeline.initializeSinglePlayerGame(
			gameId,
			players
		); // initialize my state, send to all players

		// await updatePlayerIds(players);

		function startGameHelper() {
			controller_news.flipNews(gameState, stateChangeEvent.ticker);
		}

		await this.logGameEvent(gameState, stateChangeEvent, startGameHelper);

		return gameState;
	}

	async startSinglePlayerGameLocal(
		gameId: number,
		players: any[],
		stateChangeEvent: StateChangeEvent
	) {
		const gameState = controller_timeline.initializeSinglePlayerGame(
			gameId,
			players
		);

		// await updatePlayerIds(players);

		function startGameHelper() {
			controller_news.flipNews(gameState, stateChangeEvent.ticker);
		}

		await this.logGameEvent(gameState, stateChangeEvent, startGameHelper);

		return gameState;
	}

	async startSinglePlayerFallGameLocal(
		gameId: number,
		players: any[],
		stateChangeEvent: StateChangeEvent
	) {
		const isFall = true;
		const gameState = controller_timeline.initializeSinglePlayerGame(
			gameId,
			players,
			isFall
		);

		// await updatePlayerIds(players);

		function startGameHelper(gameState: GameState, ticker: string[]) {
			controller_news.flipNews(gameState, ticker);
		}

		await this.logGameEvent(gameState, stateChangeEvent, startGameHelper);

		return gameState;
	}

	async startTutorial(
		gameId: number,
		players: any[],
		stateChangeEvent: StateChangeEvent
	) {
		const gameState = controller_timeline.initializeTutorial(
			gameId,
			players
		);

		// await updatePlayerIds(players);

		function startGameHelper(gameState: GameState, ticker: string[]) {
			gameState.tutorialStep = 1;

			ticker.push(...TUTORIAL_BLURBS);
		}

		await this.logGameEvent(gameState, stateChangeEvent, startGameHelper);

		return gameState;
	}

	// for now we will assume the phrase "endTurn" means pass action to array for gameState processing
	async endTurn(playerData: any, gameManager: GameManager): Promise<boolean> {
		const { playerId } = playerData;
		// const pState = getPlayerStateWithId(gameState.playerStateArray, playerId);
		if (gameManager.gameState.activePlayerId !== playerId) {
			console.log('Player attempted to play out of order');
			return false;
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			controller_timeline.advance,
			endTurn
		);
	}

	/* PLAYER MAT ACTIONS */
	async buyBattery(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		// send along an identifying id to make sure this is coming from the right person
		const { playerId, paymentObject } = playerData;
		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);

		if (!act_payment.isPaymentValid(pState, paymentObject)) return false;

		function buyBatteryHelper() {
			act_player.buyBattery(pState);
			act_payment.payForAction(pState, paymentObject);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			buyBatteryHelper,
			buyBattery
		);
	}

	async buyFactory(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		// send along an identifying id to make sure this is coming from the right person
		const { playerId, paymentObject } = playerData;
		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);
		if (!act_payment.isPaymentValid(pState, paymentObject)) {
			return false;
		}

		// insert test to flag if a factory was bought with just factories

		function buyFactoryHelper() {
			act_player.buyFactory(pState);

			//  carbon exclusive payment for factories
			if (
				paymentObject.factoryEnergyArr.length === 0 &&
				paymentObject.batteryEnergyArr.length === 0 &&
				paymentObject.carbonReserve > 0
			) {
				pState.factoriesBuiltThisSeasonFromCarbonOnly += 1;
			}

			act_payment.payForAction(pState, paymentObject);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			buyFactoryHelper,
			buyFactory
		);
	}

	async buyPR(playerData: any, gameManager: GameManager): Promise<boolean> {
		const { playerId, paymentObject } = playerData;
		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);

		console.log('trying buyPR');

		if (!act_payment.isPaymentValid(pState, paymentObject)) return false;

		console.log('payment valid');

		function buyPrHelper() {
			const firstEnergy: energyColors[] = [];
			if (paymentObject.factoryEnergyArr.length > 0) {
				firstEnergy.push(paymentObject.factoryEnergyArr[0]); // if from factory energy, shift
			} else if (paymentObject.batteryEnergyArr.length > 0) {
				firstEnergy.push(paymentObject.batteryEnergyArr[0]); // if from battery energy, shift,
			} else {
				firstEnergy.push(pState.energyColor); // if from carbon, player energy color
			}

			console.log('firstEnergy');
			console.log(firstEnergy);

			act_player.buyPR(pState, firstEnergy);

			act_payment.payForAction(pState, paymentObject);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			buyPrHelper,
			buyPR
		);
	}

	async donateEnergy(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('I am donating so hard right now');
		console.log(playerData);
		const { playerId, paymentObject } = playerData;
		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);

		if (!act_payment.isPaymentValid(pState, paymentObject)) return false;

		function donateEnergyHelper() {
			act_player.donateEnergy(pState, paymentObject);
			act_payment.payForAction(pState, paymentObject);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			donateEnergyHelper,
			donateEnergy
		);
	}

	async stealCarbon(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		const { playerId } = playerData;
		const { gameState } = gameManager;

		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);

		if (
			pState.seasonalCards.length <= 0 ||
			pState.modifiers.includes(carbonStolenThisTurn) ||
			gameState.carbonReservoir <= 0
		)
			return false;

		function stealCarbonHelper() {
			const stolenCardIndex = getRandomArrayIndex(pState.seasonalCards);

			// useful if you ever implement a discard pile
			const stolenCard = pState.seasonalCards.splice(stolenCardIndex, 1);

			if (stolenCard.length > 0) {
				gameState.discardPile.push(stolenCard[0]);
			}

			pState.modifiers.push(carbonStolenThisTurn);

			gameState.carbonReservoir--;
			pState.carbonReserve++;
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			stealCarbonHelper,
			stealCarbon
		);
	}

	/**
	 * Pops an energy from a player's energy array and
	 * pushes it to a player's battery energy array (if enough space)
	 */
	async storeEnergy(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		const { playerId } = playerData;
		const pState = getPlayerStateWithId(
			gameManager.gameState.playerStateArray,
			playerId
		);

		function stealCarbonHelper() {
			act_player.storeEnergy(pState);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			stealCarbonHelper,
			storeEnergy
		);
	}

	/* CARD ACTIONS */
	async playCard(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		// send along an identifying id to make sure this is coming from the right person
		console.log('start playCard');
		console.log('gameState');
		console.log(gameManager.gameState);
		console.log('playerData');
		console.log(playerData);
		const { playerId, cardId, paymentObject } = playerData;
		const { gameState } = gameManager;
		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		if (!act_payment.isPaymentValid(pState, paymentObject)) {
			return false;
		}

		function playCardHelper() {
			act_payment.payForAction(pState, paymentObject); // TODO should have a check cost for action here

			if (!controller_card.playCardById(playerData, gameState)) {
				act_payment.undoPayForAction(pState, paymentObject);
				return false;
			}

			return true;
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			playCardHelper,
			playCard + ' ' + cardId,
			false
		);

		// await this.addActionToTicker(pState, ticker, playCard + ' ' + cardId);
	}

	/**
	 * Activates equipment card's ability. Only works for equipment cards
	 * that have been previously played, are present on the table
	 * and are in a playable state (typically turn after played)
	 */
	async playEquipmentById(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('start playEquipment');

		const { playerId, cardId } = playerData;

		function playEquipmentByIdHelper() {
			controller_card.playEquipmentCardById(
				playerData,
				gameManager.gameState
			);
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			playEquipmentByIdHelper,
			playEquipmentById + ' ' + cardId
		);

		// const playEquipmentSuccess = controller_card.playEquipmentCardById(
		//     playerData,
		//     gameState
		// );
		// const pState = getPlayerStateWithId(
		//     gameState.playerStateArray,
		//     playerId
		// );
		// if (playEquipmentSuccess) {
		//     await this.addActionToTicker(pState, ticker, playEquipmentById);
		// }
	}

	async playSummitCard(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('playSummitCard');
		// send along an identifying id to make sure this is coming from the right person
		console.log('start playSummitCard');
		const { playerId, cardId, paymentObject } = playerData;
		const { gameState } = gameManager;

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);

		console.log('cardId');
		console.log(cardId);
		console.log(playerData);

		if (!act_payment.isPaymentValid(pState, paymentObject)) {
			return false;
		}

		function playSummitCardHelper() {
			act_payment.payForAction(pState, paymentObject);

			// Annoying but necessary. Have to check card validity after it's been paid for if it modify's player energy
			if (!controller_card.playSummitCardById(playerData, gameState)) {
				console.log('play summit card failed');
				act_payment.undoPayForAction(pState, paymentObject);
				return false;
			}
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			playSummitCardHelper,
			playSummitCard + ' ' + cardId
		);
	}

	/* DECISIONS REQUESTED VIA SERVER */
	async makeDecision(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('start makeDecision');
		const { playerId } = playerData;
		const { gameState } = gameManager;
		const ticker: string[] = [];

		async function makeDecisionHelper() {
			// TODO card will have decisionArr of functions, chosen by client
			const isAdvanceTimeline =
				await controller_card.resolvePlayerDecision(
					playerData,
					gameState,
					ticker
				);

			console.log('isAdvanceTimeline');
			console.log(isAdvanceTimeline);

			if (isAdvanceTimeline) {
				await controller_timeline.advance(gameState, playerId, ticker);
			}
		}

		const stateChanged = await this.logGameStateChange(
			gameManager,
			playerId,
			makeDecisionHelper,
			`Player: ${playerId} ${makeDecision}`
		);

		if (!stateChanged) {
			return false;
		}

		this.appendTickerToLatestStateChange(gameManager, ticker);
		return true;
	}

	async makeDecisionSummitDraftCardId(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('start makeDecisionSummitDraftCardId');
		const { playerId, cardId } = playerData;
		const { gameState } = gameManager;
		const ticker: string[] = [];

		async function makeDecisionSummitDraftCardId() {
			console.log(
				`Player: ${playerId} has chosen a summit card with id: ${cardId}`
			);
			// ticker.unshift(`Player: ${playerId} has chosen a summit card`); // ticker.unshift(`Player: ${playerId} has chosen a summit card with id: ${cardId}`);
			const isDraftComplete = await chooseSummitCard(
				playerData,
				gameState,
				ticker
			);

			console.log('isDraftComplete dingus ', isDraftComplete);

			if (isDraftComplete) {
				await setStateForPRTurnDecision(gameState, ticker);
			}
		}

		return await this.logGameStateChange(
			gameManager,
			playerId,
			makeDecisionSummitDraftCardId,
			' has chosen a summit card'
		);
	}

	/**
	 * Why not make a per-decision socket route.
	 *
	 * I need to recieve something different per type of decision, so I'll make a socketRoute
	 * where each of the above socket.on's goes to a function that updates the state separtely
	 * so I can better view what decisions are being made from the frontend
	 */
	async makeDecisionTurnOrder(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('start makeDecisionTurnOrder');
		const { playerId } = playerData;
		const { gameState } = gameManager;
		const ticker: string[] = [];

		async function makeDecisionTurnOrderHelper() {
			await act_com.updateTurnOrderDecision(playerData, gameState);
			await controller_news.flipNews(gameState, ticker);
		}

		const stateChanged = await this.logGameStateChange(
			gameManager,
			playerId,
			makeDecisionTurnOrderHelper,
			' decided turn order'
		);

		if (!stateChanged) {
			return false;
		}

		this.appendTickerToLatestStateChange(gameManager, ticker);
		return true;
	}

	async advanceSummitResolution(
		playerData: any,
		gameManager: GameManager
	): Promise<boolean> {
		console.log('start advanceSummitResolution');
		const { playerId } = playerData;
		const { gameState } = gameManager;
		const ticker: string[] = [];

		async function advanceSummitResolutionHelper() {
			await timeline_resolution_winter.resolveCurrentWinterContractOrAward(
				playerData,
				gameState,
				ticker
			);

			if (gameState.turnType === decisionCardResolution) {
				return;
			}

			console.log('past resolveCurrentWinterContractOrAward');

			// await this.addActionToTicker(pState, ticker, ' decided turn order');
			await controller_timeline.advance(gameState, playerId, ticker);

			console.log('past advance');
		}

		const stateChanged = await this.logGameStateChange(
			gameManager,
			playerId,
			advanceSummitResolutionHelper,
			' decided turn order'
		);

		if (!stateChanged) {
			return false;
		}

		this.appendTickerToLatestStateChange(gameManager, ticker);
		return true;
	}

	// each action is a singular dive down to a state change
	// returns true/false whether state has changed at all
	async logAdminGameStateChange(
		playerId: number,
		gameManager: GameManager,
		adminState: GameState
	): Promise<boolean> {
		const { gameState } = gameManager;
		const { forward, reverse } = getTwoWayDiffCustom(gameState, adminState);

		// test if state change has occured
		if (
			Object.keys(forward).length === 0 &&
			Object.keys(forward).length === 0
		) {
			return false;
		}

		const pState = getPlayerStateWithId(
			gameState.playerStateArray,
			playerId
		);
		const tickerText = `Player ${pState.playerId} (${pState.username}) Performed Admin State Change`;

		gameManager.gameState = applyPatchCustom(gameState, forward);

		const stateChangeEvent = createStateChangeEvent();
		stateChangeEvent.ticker.push(tickerText);
		stateChangeEvent.prev_patch = reverse;
		stateChangeEvent.next_patch = forward;
		gameManager.stateChangeHistory.push(stateChangeEvent);

		return true;
	}

	async adminUpdateState(
		playerId: number,
		gameManager: GameManager,
		gameState: GameState
	) {
		return await this.logAdminGameStateChange(
			playerId,
			gameManager,
			gameState
		);
	}
}

export const controller_game = new GameController();
